import { errorHandler } from "./error.service";
import { http, URL } from "./http.service";

// ========== CONFIGURATION MANAGEMENT ==========

export const getLogisticsConfig = async (businessId) => {
  try {
    let resp = await http.get(`${URL}/logistics/${businessId}/config`);
    return resp;
  } catch (error) {
    console.error("Error fetching logistics config:", error);
    errorHandler(error);
    throw error;
  }
};

export const addAPICredentials = async (businessId, payload) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/config/api`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error adding API credentials:", error);
    errorHandler(error);
    throw error;
  }
};
export const getWebhookEvents = async (businessId, payload) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/webhooks/events`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error adding API credentials:", error);
    errorHandler(error);
    throw error;
  }
};

export const configureCarrier = async (businessId, payload) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/config/carrier`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error configuring carrier:", error);
    errorHandler(error);
    throw error;
  }
};

export const updateBusinessDetails = async (businessId, payload) => {
  try {
    let resp = await http.put(
      `${URL}/logistics/${businessId}/config/business-details`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error updating business details:", error);
    errorHandler(error);
    throw error;
  }
};

export const testAPIConnection = async (businessId, payload) => {
  try {
    // Format payload correctly
    const formattedPayload = {
      apiProvider: payload.apiProvider,
      apiKey: payload.apiKey,
      apiSecret: payload.apiSecret,
    };

    let resp = await http.post(
      `${URL}/logistics/${businessId}/config/test-api`,
      formattedPayload,
    );
    return resp;
  } catch (error) {
    console.error("Error testing API connection:", error);
    errorHandler(error);
    throw error;
  }
};

export const getAPIConfigStatus = async (businessId) => {
  try {
    let resp = await http.get(`${URL}/logistics/${businessId}/config/status`);
    return resp;
  } catch (error) {
    console.error("Error getting API config status:", error);
    errorHandler(error);
    throw error;
  }
};

export const removeAPICredentials = async (businessId, provider) => {
  try {
    let resp = await http.delete(
      `${URL}/logistics/${businessId}/config/api/${provider}`,
    );
    return resp;
  } catch (error) {
    console.error("Error removing API credentials:", error);
    errorHandler(error);
    throw error;
  }
};

export const removeCarrierConfig = async (businessId, carrierId) => {
  try {
    let resp = await http.delete(
      `${URL}/logistics/${businessId}/config/carrier/${carrierId}`,
    );
    return resp;
  } catch (error) {
    console.error("Error removing carrier config:", error);
    errorHandler(error);
    throw error;
  }
};

export const getBusinessFromMainDB = async (businessId) => {
  console.log("businessId getBusinessFromMainDB", businessId);
  try {
    let resp = await http.get(
      `${URL}/logistics/${businessId}/config/business-from-main`,
    );
    return resp;
  } catch (error) {
    console.error("Error fetching business from main DB:", error);
    errorHandler(error);
    throw error;
  }
};

export const syncFromMainBusiness = async (businessId) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/config/sync-from-main-business`,
    );
    return resp;
  } catch (error) {
    console.error("Error syncing from main business:", error);
    errorHandler(error);
    throw error;
  }
};

// ========== CARRIER MANAGEMENT ==========

export const getAvailableCarriers = async (businessId) => {
  try {
    let resp = await http.get(`${URL}/logistics/${businessId}/carriers`);
    return resp;
  } catch (error) {
    console.error("Error fetching carriers:", error);
    errorHandler(error);
    throw error;
  }
};

export const getCarrierSupport = async (businessId) => {
  try {
    let resp = await http.get(`${URL}/logistics/${businessId}/carrier-support`);
    return resp;
  } catch (error) {
    console.error("Error fetching carrier support:", error);
    errorHandler(error);
    throw error;
  }
};

// ========== SHIPMENT MANAGEMENT ==========

export const createShipment = async (businessId, payload) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/create-shipment`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error creating shipment:", error);
    errorHandler(error);
    throw error;
  }
};

export const cancelShipment = async (businessId, orderId) => {
  try {
    let resp = await http.delete(
      `${URL}/logistics/${businessId}/shipments/${orderId}`,
    );
    return resp;
  } catch (error) {
    console.error("Error cancelling shipment:", error);
    errorHandler(error);
    throw error;
  }
};

// ========== TRACKING ==========

export const trackShipment = async (businessId, orderId) => {
  try {
    let resp = await http.get(
      `${URL}/logistics/${businessId}/track/${orderId}`,
    );
    return resp;
  } catch (error) {
    console.error("Error tracking shipment:", error);
    errorHandler(error);
    throw error;
  }
};

export const trackRealtime = async (businessId, trackingNumber, carrier) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/track-realtime`,
      {
        trackingNumber,
        carrier,
      },
    );
    return resp;
  } catch (error) {
    console.error("Error with real-time tracking:", error);
    errorHandler(error);
    throw error;
  }
};

// Update the trackByNumber function
export const trackByNumber = async (trackingNumber, carrier) => {
  try {
    // First try to track with business context if available
    const businessId = localStorage.getItem("currentBusinessId");

    if (businessId) {
      try {
        const response = await trackRealtime(
          businessId,
          trackingNumber,
          carrier,
        );
        if (response.data.success) {
          return response;
        }
      } catch (error) {
        console.log("Business tracking failed, trying public API");
      }
    }

    // Use public tracking endpoint
    let resp = await http.get(`${URL}/logistics/track/${trackingNumber}`, {
      params: { carrier },
    });

    return resp;
  } catch (error) {
    console.error("Error tracking by number:", error);
    errorHandler(error);
    throw error;
  }
};

// ========== SHIPMENT STATUS MANAGEMENT ==========

export const updateShipmentStatus = async (businessId, orderId, payload) => {
  try {
    let resp = await http.put(
      `${URL}/logistics/${businessId}/update-status/${orderId}`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error updating shipment status:", error);
    errorHandler(error);
    throw error;
  }
};

// ========== BULK OPERATIONS ==========

export const getAllShipments = async (businessId, params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    let resp = await http.get(
      `${URL}/logistics/${businessId}/all?${queryString}`,
    );
    return resp;
  } catch (error) {
    console.error("Error fetching shipments:", error);
    errorHandler(error);
    throw error;
  }
};

export const getShipmentAnalytics = async (businessId, params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    let resp = await http.get(
      `${URL}/logistics/${businessId}/analytics?${queryString}`,
    );
    return resp;
  } catch (error) {
    console.error("Error fetching analytics:", error);
    errorHandler(error);
    throw error;
  }
};

// ========== SHIPPING RATES ==========

export const getShippingRates = async (businessId, payload) => {
  try {
    let resp = await http.post(`${URL}/logistics/${businessId}/rates`, payload);
    return resp;
  } catch (error) {
    console.error("Error getting shipping rates:", error);
    errorHandler(error);
    throw error;
  }
};
export const calculateUnifiedShipping = async (businessId, payload) => {
  try {
    console.log("calculateUnifiedShipping payload:", payload);

    // Make sure payload is a proper object, not FormData
    const jsonPayload = {
      destination: {
        pincode:
          payload.destination?.pincode || payload["destination[pincode]"],
        city: payload.destination?.city || payload["destination[city]"],
        state: payload.destination?.state || payload["destination[state]"],
        country:
          payload.destination?.country ||
          payload["destination[country]"] ||
          "IN",
      },
      weight: payload.weight,
      orderValue: payload.orderValue,
      shippingConfig: payload.shippingConfig,
    };

    console.log("JSON payload to send:", jsonPayload);

    let resp = await http.post(
      `${URL}/logistics/${businessId}/calculation`,
      jsonPayload,
      {
        headers: {
          "Content-Type": "application/json", // Explicitly set JSON header
        },
      },
    );
    return resp.data;
  } catch (error) {
    console.error("Error calculating unified shipping:", error);
    errorHandler(error);
    throw error;
  }
};
// Delivery Management APIs
export const createDelivery = async (businessId, payload) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/deliveries/create`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error creating delivery:", error);
    errorHandler(error);
    throw error;
  }
};

export const getDeliveryDetails = async (businessId, orderId) => {
  try {
    let resp = await http.get(
      `${URL}/logistics/${businessId}/deliveries/${orderId}/details`,
    );
    return resp;
  } catch (error) {
    console.error("Error getting delivery details:", error);
    errorHandler(error);
    throw error;
  }
};

export const updateDeliveryStatus = async (businessId, orderId, payload) => {
  try {
    let resp = await http.put(
      `${URL}/logistics/${businessId}/deliveries/${orderId}/status`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error updating delivery status:", error);
    errorHandler(error);
    throw error;
  }
};

export const getPendingDeliveries = async (businessId, params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    let resp = await http.get(
      `${URL}/logistics/${businessId}/deliveries/pending?${queryString}`,
    );
    return resp;
  } catch (error) {
    console.error("Error getting pending deliveries:", error);
    errorHandler(error);
    throw error;
  }
};

export const bulkUpdateDeliveries = async (businessId, payload) => {
  try {
    let resp = await http.post(
      `${URL}/logistics/${businessId}/deliveries/bulk-update`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error bulk updating deliveries:", error);
    errorHandler(error);
    throw error;
  }
};
// ========== UTILITY FUNCTIONS ==========

export const detectCarrierFromTracking = (trackingNumber) => {
  const patterns = {
    delhivery: /^DEL/i,
    bluedart: /^BLD/i,
    dtdc: /^DTDC/i,
    indiapost: /^[A-Z]{2}\d{9}[A-Z]{2}$/i,
    ecomexpress: /^ECOM/i,
    shadowfax: /^SFX/i,
    xpressbees: /^XPB/i,
    fedex: /^\d{12}$/,
    ups: /^1Z[A-Z0-9]{16}$/i,
  };

  for (const [carrier, pattern] of Object.entries(patterns)) {
    if (pattern.test(trackingNumber)) {
      return carrier;
    }
  }

  return "delhivery";
};

export const validateTrackingNumber = (trackingNumber, carrier) => {
  const validations = {
    delhivery: /^DEL[A-Z0-9]{9}IN$/i,
    bluedart: /^BLD[A-Z0-9]{9}IN$/i,
    dtdc: /^DTDC[A-Z0-9]{9}IN$/i,
    indiapost: /^[A-Z]{2}\d{9}[A-Z]{2}$/i,
    fedex: /^\d{12}$/,
    ups: /^1Z[A-Z0-9]{16}$/i,
  };

  const pattern = validations[carrier];
  return pattern ? pattern.test(trackingNumber) : true;
};

export const getMockTrackingData = (trackingNumber, carrier) => {
  const baseTime = new Date();
  const events = [
    {
      status: "label_created",
      description: "Shipping label created",
      location: "Warehouse",
      timestamp: new Date(baseTime - 4 * 60 * 60 * 1000).toISOString(),
    },
    {
      status: "picked_up",
      description: "Package picked up by carrier",
      location: "Origin City",
      timestamp: new Date(baseTime - 3 * 60 * 60 * 1000).toISOString(),
    },
    {
      status: "in_transit",
      description: "Package in transit to destination",
      location: "Sorting Center",
      timestamp: new Date(baseTime - 2 * 60 * 60 * 1000).toISOString(),
    },
  ];

  return {
    tracking_number: trackingNumber,
    carrier: carrier,
    carrier_name: carrier.charAt(0).toUpperCase() + carrier.slice(1),
    status: events[events.length - 1].status,
    events: events,
    estimated_delivery: new Date(
      baseTime.getTime() + 2 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    last_updated: new Date().toISOString(),
    source: "mock_data",
  };
};
export const calculateOrderTotal = async (businessId, items, destination) => {
  try {
    console.log("=== calculateOrderTotal service call ===");
    console.log("Business ID:", businessId);
    console.log("Items:", items);
    console.log("Destination:", destination);
    console.log("Items type:", typeof items);
    console.log("Items is array?", Array.isArray(items));

    // Ensure items is an array
    const itemsToSend = Array.isArray(items) ? items : [items];

    const response = await http.post(
      `${URL}/logistics/${businessId}/calculate-order`,
      {
        items: itemsToSend, // Send as array
        destination,
      },
    );

    console.log("API Response:", response.data);

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || "Calculation failed");
    }
  } catch (error) {
    console.error("Order calculation error in service:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

export const calculateShippingOnly = async (
  businessId,
  destination,
  orderValue,
  orderWeight,
) => {
  try {
    const response = await http.post(
      `${URL}/logistics/${businessId}/calculate-shipping`,
      {
        destination,
        orderValue,
        orderWeight,
      },
    );

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || "Shipping calculation failed");
    }
  } catch (error) {
    console.error("Shipping calculation error:", error);
    throw error;
  }
};
export const updateShippingConfig = async (businessId, payload) => {
  try {
    let resp = await http.put(
      `${URL}/logistics/${businessId}/config/shipping`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Error updating shipping config:", error);
    errorHandler(error);
    throw error;
  }
};

export const getShippingConfig = async (businessId) => {
  try {
    const timestamp = new Date().getTime();
    let resp = await http.get(
      `${URL}/logistics/${businessId}/shipping-config?t=${timestamp}`,
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      },
    );
    return resp;
  } catch (error) {
    console.error("Error fetching shipping config:", error);
    errorHandler(error);
    throw error;
  }
};
// services/logistic.service.js - Add these functions:

// Webhook registration with carriers
export const registerCarrierWebhook = async (
  carrierId,
  credentials,
  webhookUrl,
) => {
  try {
    const response = await http.post(
      `${URL}/logistics/webhooks/register-carrier`,
      {
        carrier: carrierId,
        credentials: credentials,
        webhook_url: webhookUrl,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Carrier webhook registration error:", error);
    throw error;
  }
};

// Test webhook endpoint
export const testWebhookEndpoint = async (businessId, testPayload) => {
  try {
    const resp = await http.post(
      `${URL}/logistics/${businessId}/webhooks/test`,
      testPayload,
    );
    return resp;
  } catch (error) {
    console.error("Webhook test error:", error);
    throw error;
  }
};
// Fix the setupCarrierWebhook function:
export const setupCarrierWebhook = async (businessId, payload) => {
  try {
    const resp = await http.post(
      `${URL}/logistics/${businessId}/webhooks/setup`,
      payload,
    );
    return resp;
  } catch (error) {
    console.error("Webhook setup error:", error);
    throw error;
  }
};

// Update getWebhookConfig to handle the response properly:
export const getWebhookConfig = async (businessId, carrierId = null) => {
  try {
    const params = {};
    if (carrierId) {
      params.carrier = carrierId;
    }

    console.log("Fetching webhook config for business:", businessId); // Debug log

    if (!businessId || businessId === "undefined") {
      throw new Error("Business ID is required");
    }

    const resp = await http.get(
      `${URL}/logistics/${businessId}/webhooks/config`,
      { params },
    );
    return resp.data; // Return the data directly
  } catch (error) {
    console.error("Get webhook config error:", error);
    throw error;
  }
};